import { useState, useEffect, useMemo } from 'react';
import { getAvailableSchools, attachSchoolToNetwork } from '@/api/school-networks';
import type { AvailableSchool } from '@/types/school-network';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchSelect } from '@/components/ui/search-select';
import { AlertTriangle, Link2, Loader2 } from 'lucide-react';

/**
 * Texte d'avertissement commun à tous les points de rattachement d'une
 * école à un réseau physique (page réseau, fiche école, déploiement V2).
 */
export function attachWarningText(schoolName: string, networkName: string, host: string): string {
  return (
    `Vous allez rattacher « ${schoolName} » au réseau physique « ${networkName} ». ` +
    `Cette opération est irréversible : l'école ne pourra ni être retirée du réseau ` +
    `ni déplacée vers un autre réseau. Ses utilisateurs se connecteront via ${host}.`
  );
}

interface AttachSchoolDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  networkId: number;
  networkName: string;
  /** Host du réseau : proposé par défaut comme host de l'école */
  networkHost: string | null;
  /** École imposée (fiche école) : le sélecteur est masqué */
  fixedSchool?: { id: number; name: string; code: string };
  onAttached: () => void;
}

export function AttachSchoolDialog({
  open,
  onOpenChange,
  networkId,
  networkName,
  networkHost,
  fixedSchool,
  onAttached,
}: AttachSchoolDialogProps) {
  const [schools, setSchools] = useState<AvailableSchool[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>(
    fixedSchool ? String(fixedSchool.id) : ''
  );
  const [host, setHost] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setWarnings([]);
    setHost(networkHost || '');
    setSelectedSchoolId(fixedSchool ? String(fixedSchool.id) : '');
    if (!fixedSchool) {
      const fetchSchools = async () => {
        try {
          setSchoolsLoading(true);
          const data = await getAvailableSchools(networkId);
          setSchools(data.filter((s) => !s.inNetwork));
        } catch (err) {
          console.error(err);
        } finally {
          setSchoolsLoading(false);
        }
      };
      fetchSchools();
    }
  }, [open, networkId, networkHost, fixedSchool]);

  const schoolOptions = useMemo(
    () =>
      schools.map((s) => ({
        value: String(s.id),
        label: `${s.name} (${s.code})`,
        searchText: `${s.name} ${s.code}`,
      })),
    [schools]
  );

  const selectedSchool = fixedSchool
    ? { name: fixedSchool.name, code: fixedSchool.code }
    : schools.find((s) => String(s.id) === selectedSchoolId);

  const canSubmit = !!selectedSchoolId && !!host.trim() && !attaching;

  const handleConfirm = async () => {
    setAttaching(true);
    setError(null);
    try {
      const result = await attachSchoolToNetwork(networkId, Number(selectedSchoolId), host.trim());
      setConfirming(false);
      if (result.warnings.length > 0) {
        setWarnings(result.warnings);
      } else {
        onOpenChange(false);
      }
      onAttached();
    } catch (err: any) {
      setConfirming(false);
      setError(err?.response?.data?.error || 'Erreur lors du rattachement');
    } finally {
      setAttaching(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !attaching && onOpenChange(o)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Rattacher une école au réseau « {networkName} »
            </DialogTitle>
            <DialogDescription>
              Le rattachement à un réseau physique est <strong>définitif</strong> : l'école ne
              pourra ni être retirée ni déplacée vers un autre réseau.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {!fixedSchool && (
              <div className="space-y-2">
                <Label>École</Label>
                {schoolsLoading ? (
                  <p className="text-sm text-muted-foreground">Chargement des écoles...</p>
                ) : (
                  <SearchSelect
                    options={schoolOptions}
                    value={selectedSchoolId}
                    onValueChange={setSelectedSchoolId}
                    placeholder="Sélectionner une école..."
                    searchPlaceholder="Rechercher une école..."
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  Seules les écoles sans réseau physique sont proposées.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="attach-host">Domaine (host) de l'école</Label>
              <Input
                id="attach-host"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder={networkHost || 'ecole.mon-reseau.fr'}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Prérempli avec le domaine du réseau. Ce domaine alimente la liste des hosts du
                group tenant V2.
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {warnings.length > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Rattachement effectué avec des avertissements :
                  <ul className="mt-1 list-disc pl-4">
                    {warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={attaching}>
              {warnings.length > 0 ? 'Fermer' : 'Annuler'}
            </Button>
            {warnings.length === 0 && (
              <Button onClick={() => setConfirming(true)} disabled={!canSubmit}>
                {attaching ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Rattachement...
                  </>
                ) : (
                  'Rattacher'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation irréversible */}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Rattachement définitif
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedSchool &&
                attachWarningText(selectedSchool.name, networkName, host.trim())}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={attaching}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={attaching}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {attaching ? 'Rattachement...' : 'Rattacher définitivement'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
